import { useNavigate } from 'react-router';

/** Anything inside a row that owns its own click. */
const INTERACTIVE = 'a, button, input, textarea, select, label, [role="button"], [contenteditable]';

/**
 * Makes a whole table row open the page it describes.
 *
 * Every list here already links from the one cell that names the row — the product's name, the
 * order's number. That link stays: it is what a screen reader announces, what the keyboard tabs
 * to, and what a middle click or a ⌘-click opens in a new tab. A `<tr>` cannot be a link, and
 * wrapping every cell in one would put a dozen tab stops on every row.
 *
 * What this adds is the behaviour a reader expects from a list of things: the row is a target, not
 * a strip of text with one live word in it. Hence a pointer over the whole row and a click
 * anywhere in it, on top of the link rather than instead of it.
 *
 * Three clicks are deliberately left alone:
 *
 *   - one that lands on something interactive — the name's own link, a row action, a checkbox;
 *   - one that ends a text selection, so a copied SKU does not navigate away mid-drag;
 *   - one carrying a modifier, which belongs to the browser: the real link is what knows how to
 *     open in a new tab, and a script that guesses at it gets the history wrong.
 */
export function useRowLink() {
  const navigate = useNavigate();

  return (to: string) => ({
    className: 'is-clickable',
    onClick(event: React.MouseEvent<HTMLTableRowElement>) {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      if ((event.target as HTMLElement).closest(INTERACTIVE)) {
        return;
      }

      if (!window.getSelection()?.isCollapsed) {
        return;
      }

      navigate(to);
    },
  });
}
